import { useState } from 'react'
import { useCreateCalibrationProfile } from '../../hooks/useCalibrationProfiles'
import { useAuth } from '../../hooks/useAuth'
import { performRegression, validateRegressionData, RegressionDataPoint } from '../../lib/regression'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Alert, AlertDescription } from '../ui/alert'

interface CalibrationHelperProps {
  onProfileCreated: () => void
}

export function CalibrationHelper({ onProfileCreated }: CalibrationHelperProps) {
  const { user } = useAuth()
  const createProfileMutation = useCreateCalibrationProfile()
  const [csvData, setCsvData] = useState('')
  const [profileName, setProfileName] = useState('')
  const [regressionResults, setRegressionResults] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAnalyze = () => {
    try {
      setError(null)
      
      // Parse CSV data
      const data = parseCsvData(csvData)
      
      // Validate data
      const validation = validateRegressionData(data)
      if (!validation.isValid) {
        setError(validation.message!)
        return
      }
      
      // Perform regression
      const results = performRegression(data)
      setRegressionResults(results)
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze data')
    }
  }

  const handleCreateProfile = async () => {
    if (!regressionResults || !user) return

    try {
      await createProfileMutation.mutateAsync({
        user_id: user.id,
        name: profileName,
        beta0_base_pace_min_per_km: regressionResults.coefficients.beta0,
        beta1_uphill_min_per_m_per_km: regressionResults.coefficients.beta1,
        beta2_downhill_min_per_m_per_km: regressionResults.coefficients.beta2,
        beta3_fatigue_min_per_km: regressionResults.coefficients.beta3 || 0,
        use_fatigue: regressionResults.useFatigue,
        // Keep default nutrition targets
        target_carbs_g_per_h_low: 60.0,
        target_carbs_g_per_h_high: 90.0,
        target_fluids_ml_per_h_low: 500.0,
        target_fluids_ml_per_h_high: 750.0,
        target_sodium_mg_per_h_low: 300.0,
        target_sodium_mg_per_h_high: 800.0
      })
      
      onProfileCreated()
    } catch (err) {
      setError('Failed to create calibration profile')
    }
  }

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-primary-900">Calibration Helper</CardTitle>
          <p className="text-sm text-neutral-600">
            Paste your historical run data to create a personalized pace model
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="csvData" className="text-neutral-700 font-medium">Run Data (CSV format)</Label>
            <Textarea
              id="csvData"
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              placeholder="distance_km,elev_gain_m,elev_loss_m,actual_time_min
5.0,150,50,32.5
3.2,80,120,18.0
..."
              rows={8}
              className="font-mono text-sm mt-2 bg-neutral-50 border-neutral-300"
            />
            <p className="text-xs text-neutral-500 mt-2">
              Required: distance_km, elev_gain_m, elev_loss_m, actual_time_min (one row per segment)
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-accent-200 bg-accent-50">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button onClick={handleAnalyze} disabled={!csvData.trim()} size="lg">
            Analyze Data
          </Button>
        </CardContent>
      </Card>

      {regressionResults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl font-bold text-primary-900">Analysis Results</CardTitle>
            <p className="text-sm text-neutral-600">
              Your personalized pace coefficients based on {regressionResults.dataPoints} data points
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-neutral-50 p-4 rounded-xl">
                <Label className="text-neutral-700 font-medium">Base Pace (β0)</Label>
                <div className="font-mono text-lg text-primary-900 mt-1">
                  {regressionResults.coefficients.beta0.toFixed(3)} min/km
                </div>
              </div>
              <div className="bg-neutral-50 p-4 rounded-xl">
                <Label className="text-neutral-700 font-medium">Uphill Factor (β1)</Label>
                <div className="font-mono text-lg text-primary-900 mt-1">
                  {(regressionResults.coefficients.beta1 * 100).toFixed(2)} s per 100m/km
                </div>
              </div>
              <div className="bg-neutral-50 p-4 rounded-xl">
                <Label className="text-neutral-700 font-medium">Downhill Factor (β2)</Label>
                <div className="font-mono text-lg text-primary-900 mt-1">
                  {(regressionResults.coefficients.beta2 * 100).toFixed(2)} s per 100m/km
                </div>
              </div>
              {regressionResults.useFatigue && (
                <div className="bg-neutral-50 p-4 rounded-xl">
                  <Label className="text-neutral-700 font-medium">Fatigue Factor (β3)</Label>
                  <div className="font-mono text-lg text-primary-900 mt-1">
                    {(regressionResults.coefficients.beta3 * 1000).toFixed(2)} s per km
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-primary-50 p-4 rounded-xl border border-primary-100">
                <Label className="text-primary-700 font-medium">Model Fit (R²)</Label>
                <div className="font-mono text-2xl text-primary-900 mt-1">
                  {regressionResults.rSquared.toFixed(3)}
                  <span className="text-sm ml-2 text-primary-600 font-normal">
                    ({regressionResults.rSquared > 0.8 ? 'Excellent' :
                      regressionResults.rSquared > 0.6 ? 'Good' :
                      regressionResults.rSquared > 0.4 ? 'Fair' : 'Poor'})
                  </span>
                </div>
              </div>
              <div className="bg-neutral-50 p-4 rounded-xl">
                <Label className="text-neutral-700 font-medium">Total Distance</Label>
                <div className="font-mono text-2xl text-primary-900 mt-1">
                  {csvData.split('\n').slice(1).reduce((sum, line) => {
                    const values = line.split(',')
                    return sum + (parseFloat(values[0]) || 0)
                  }, 0).toFixed(1)} km
                </div>
              </div>
            </div>

            {regressionResults.warnings && regressionResults.warnings.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertDescription>
                  <strong className="text-amber-800">Notes:</strong>
                  <ul className="list-disc list-inside mt-1 text-amber-700">
                    {regressionResults.warnings.map((warning: string, index: number) => (
                      <li key={index} className="text-sm">{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-3">
              <Label htmlFor="profileName" className="text-neutral-700 font-medium">Profile Name</Label>
              <Input
                id="profileName"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="e.g., My 2024 Ultra Pace"
                className="bg-white"
              />
            </div>

            <Button
              onClick={handleCreateProfile}
              disabled={!profileName.trim() || createProfileMutation.isPending}
              className="w-full"
              size="lg"
            >
              {createProfileMutation.isPending ? 'Creating Profile...' : 'Create Calibration Profile'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function parseCsvData(csvText: string): RegressionDataPoint[] {
  const lines = csvText.trim().split('\n')
  const header = lines[0].toLowerCase()
  
  if (!header.includes('distance_km') || !header.includes('actual_time_min')) {
    throw new Error('CSV must include distance_km and actual_time_min columns')
  }
  
  const data = lines.slice(1).map(line => {
    const values = line.split(',').map(v => parseFloat(v.trim()))
    return {
      distance_km: values[0],
      elev_gain_m: values[1] || 0,
      elev_loss_m: values[2] || 0,
      actual_time_min: values[3]
    }
  }).filter(row => !isNaN(row.distance_km) && !isNaN(row.actual_time_min))

  if (data.length === 0) {
    throw new Error('No valid data found. Check your CSV format.')
  }

  return data
}